import React, {
	FC,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useSearchParams } from "react-router-dom";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { UserContext, TranslationContext } from "@/app/providers";
import { useTaskPage, useTaskTableHeaderActions } from "@/features/task/hooks";
import { TaskGroup } from "@/features/task/model/Task/TaskRequest";
import {
	AddNewTaskDialog,
	EditTaskDialog,
	TaskDialogRecurring,
	useTaskHeaderActions,
	useTaskHeaderPrimaryAction,
} from "@/features/task/ui/components";
import { CustomToolbarV2, StyledDataGrid } from "@/shared/data-grid";
import { isImageAttachment } from "@/shared/lib/attachmentPreview";
import { DataGridCommentTooltip } from "@/shared/ui/DataGridCommentTooltip";
import { ImageAttachmentPreviewDialog } from "@/shared/ui/ImageAttachmentPreviewDialog";
import Spinner from "@/shared/ui/Spinner";
import ComponentError from "@/shared/ui/errors/Components";
import { useSelectedTaskGroups } from "@/storage/useSelectedTaskGroups";
import {
	sharedAutoHeightDataGridSectionSx,
	sharedTableCheckboxSx,
	sharedTableTagChipSx,
} from "@/styles/sharedDataGridStyles";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import SearchIcon from "@mui/icons-material/Search";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	Link,
	MenuItem,
	Select,
	Snackbar,
	TablePagination,
	TextField,
	Tooltip,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import rehypeRaw from "rehype-raw";

import { SortDirection } from "@/model/SortDirection";
import { UserSummary } from "@/model/User/UserSummary";

import { checkAdminAccess } from "@/service/ApplicationQueries";
import { useFetchShiftSystems } from "@/service/ShiftHomeQueries";
import {
	useFetchTaskById,
	useFetchTaskGroups,
	useFetchTasks,
} from "@/service/TaskQueries";
import { useFetchUserSummariesByCurrentCompany } from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";


/* ────────────────────── Pastel palette (consistent with other screens) ────────────────────── */
const COL = {
	cardBg: (t: Theme) => t.palette.background.paper,
	cardBorder: (t: Theme) => t.palette.divider,
	headerBand: (t: Theme) =>
		t.palette.mode === "light" ? "#F7F7F9" : t.palette.background.default,
	rowAlt: (t: Theme) =>
		t.palette.mode === "light" ? "#FCFCFD" : t.palette.background.default,
	hover: (t: Theme) => (t.palette.mode === "light" ? "#F6F7FA" : "#1f2937"),
	chipBg: "#F1F5F9",
	tagBg: "#EEF2FF",
	tagText: "#3730A3",
};

interface TaskAttachmentDto {
	Url?: string;
	FileName?: string;
}

interface TaskCommentDto {
	Text?: string;
	Value?: string;
	Created?: string | Date;
	Date?: string | Date;
}

interface TaskUserDto {
	Id?: number;
	Name?: string;
}

interface TaskRecurringConfigurationDto {
	Frequency?: string;
	ReccuringTemplateId?: number;
}

interface TaskDto {
	TaskId: number;
	TaskGroupId?: number;
	Title: string;
	Deadline?: string | Date | null;
	Description?: string;
	Status?: string;
	LastChanged?: string | Date | null;
	Reccuring?: boolean;
	ReccuringConfiguration?: TaskRecurringConfigurationDto | null;
	Shiftlog?: boolean;
	Handover?: boolean;
	User?: TaskUserDto | null;
	Tags?: string[];
	Attachment?: TaskAttachmentDto | null;
	Comments?: TaskCommentDto[];
	GroupResponsibleList?: number[];
	UserResponsibleList?: number[];
	RecurringTasks?: TaskDto[];
}

interface TaskGroupsResponse {
	Groups: TaskGroup[];
}

interface TasksResponse {
	Items: TaskDto[];
	Total: number;
	Page: number;
	PageSize: number;
	HasNextPage: boolean;
}

interface LegacyTasksResponse {
	Tasks?: TaskDto[];
	Total?: number;
}

interface TaskRow {
	id: number;
	title: string;
	deadline?: string | Date | null;
	description: string;
	status: string;
	lastChanged: Date | null;
	recurring: "Yes" | "No";
	frequency: string;
	shiftlog: "Yes" | "No";
	handover: "Yes" | "No";
	user: string;
	tags: string[];
	attachment: TaskAttachmentDto | null;
	comments: TaskCommentDto[];
	groupName: string;
	completed?: string | Date | null;
	completedBy?: { Id?: number; Name?: string; Email?: string } | null;
	selectedSystem?: number | null;
	groupResponsibleCount: number;
	userResponsibleCount: number;
	responsibleGroups: string[];
	responsibleUsers: string[];
	recurringTemplateId?: number | null;
	recurringChildren?: RecurringChildSummary[];
}

type RecurringChildSummary = {
	id: number;
	title: string;
	status?: string;
	deadline?: string | Date | null;
};

interface TaskCellParams {
	row: TaskRow;
	value?: unknown;
}

type TaskGroupsPayload = TaskGroupsResponse | TaskGroup[];
type TasksPayload = TasksResponse | LegacyTasksResponse;

const isTaskGroupsResponse = (
	payload: TaskGroupsPayload | undefined,
): payload is TaskGroupsResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		!Array.isArray(payload) &&
		Array.isArray(payload.Groups),
	);

const isTasksResponse = (
	payload: TasksPayload | undefined,
): payload is TasksResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		"Items" in payload &&
		Array.isArray(payload.Items),
	);

const isLegacyTasksResponse = (
	payload: TasksPayload | undefined,
): payload is LegacyTasksResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		"Tasks" in payload &&
		Array.isArray(payload.Tasks),
	);

const formatDisplayName = (name?: string) => {
	const trimmedName = name?.trim();
	if (!trimmedName) return "";

	return trimmedName
		.toLowerCase()
		.replace(/(^|[\s._-])([a-z])/g, (_, separator: string, letter: string) => {
			return `${separator}${letter.toUpperCase()}`;
		});
};

const extractGridValue = (input: unknown): unknown => {
	if (typeof input === "object" && input !== null && "value" in input) {
		return (input as { value?: unknown }).value;
	}
	return input;
};

interface TaskCardProps {
	title: string;
}

const TaskCard: FC<TaskCardProps> = ({ title }) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:767px)");
	const taskPage = useTaskPage();
	const [searchParams, setSearchParams] = useSearchParams();
	const hasTaskPage = Boolean(taskPage);
	const setTaskHeaderActions = useTaskHeaderActions();
	const setTaskHeaderPrimaryAction = useTaskHeaderPrimaryAction();
	const sharedSearchInput = taskPage?.searchInput;
	const persistedTaskGroups = useSelectedTaskGroups();
	const selectedGroupIds = taskPage?.selectedGroupIds ?? persistedTaskGroups[0];
	const setSelectedGroupIds =
		taskPage?.setSelectedGroupIds ?? persistedTaskGroups[1];

	// Permissions
	const user = useContext(UserContext);
	const isAdmin = Boolean(user && checkAdminAccess(user));

	// Selected groups (persist)
	// Dialogs & UI state
	const [taskDialogOpen, setTaskDialogOpen] = useState(false);
	const [selectedTask, setSelectedTask] = useState<TaskRow | null>(null);
	const [recurringParentContextTaskId, setRecurringParentContextTaskId] =
		useState<number | null>(null);
	const [newComment, setNewComment] = useState("");
	const [openNewTaskDialog, setOpenNewTaskDialog] = useState(false);
	const [imageAttachmentPreview, setImageAttachmentPreview] = useState<{
		url: string;
		fileName: string;
	} | null>(null);
	const [showGroupWarning, setShowGroupWarning] = useState(false);

	// Editing
	const [taskIdToEdit, setTaskIdToEdit] = useState<number | null>(null);

	// Table & search
	const [searchTermTable, setSearchTermTable] = useState("");
	const [searchInput, setSearchInput] = useState(searchTermTable);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	// Sort state
	const [sortDirection, setSortDirection] = useState<SortDirection>(
		SortDirection.ASC,
	);

	// New Task defaults
	const [groupsNewTask, setGroupsNewTask] = useState<string[]>([
		"Administration",
	]);

	// Fetches
	const { data: usersData = [] } = useFetchUserSummariesByCurrentCompany();
	const users: UserSummary[] = usersData;
	const { data: shiftSystems = [] } = useFetchShiftSystems();
	const { data: taskGroupsData } = useFetchTaskGroups() as {
		data?: TaskGroupsPayload;
	};
	const taskGroupsStabilised = useMemo(() => {
		if (Array.isArray(taskGroupsData)) {
			return taskGroupsData;
		}
		if (isTaskGroupsResponse(taskGroupsData)) {
			return taskGroupsData.Groups;
		}
		return [];
	}, [taskGroupsData]);
	const effectiveSelectedGroupIds = useMemo(() => {
		const availableGroupIdSet = new Set(
			taskGroupsStabilised
				.map((group) => Number(group.Id))
				.filter(
					(groupId): groupId is number =>
						Number.isInteger(groupId) && groupId > 0,
				),
		);
		const normalized = selectedGroupIds
			.map((groupId) => Number(groupId))
			.filter(
				(groupId): groupId is number =>
					Number.isInteger(groupId) && groupId > 0,
			);
		return [...new Set(normalized)].filter((groupId) =>
			availableGroupIdSet.has(groupId),
		);
	}, [selectedGroupIds, taskGroupsStabilised]);
	const { data: detailedTask, isLoading: isLoadingTaskDetail } =
		useFetchTaskById(taskIdToEdit ?? 0);

	// Stable dep for tasks query
	const stableGroupIds = useMemo(
		() => effectiveSelectedGroupIds,
		[effectiveSelectedGroupIds.join(",")],
	);

	const taskResults = useFetchTasks(
		stableGroupIds,
		"Recurring",
		page,
		pageSize,
		searchTermTable,
		sortDirection,
	) as {
		data?: TasksPayload;
		isFetching: boolean;
	};
	const { data, isFetching } = taskResults;

	useEffect(() => {
		setShowGroupWarning(stableGroupIds.length === 0);
	}, [stableGroupIds.length]);

	useEffect(() => {
		if (taskGroupsStabilised.length === 0) return;

		const hasSelectionDiff =
			effectiveSelectedGroupIds.length !== selectedGroupIds.length ||
			effectiveSelectedGroupIds.some(
				(groupId, index) => groupId !== selectedGroupIds[index],
			);
		if (hasSelectionDiff) {
			setSelectedGroupIds(effectiveSelectedGroupIds);
		}
	}, [
		effectiveSelectedGroupIds,
		selectedGroupIds,
		setSelectedGroupIds,
		taskGroupsStabilised.length,
	]);

	useEffect(() => {
		if (sharedSearchInput !== undefined) {
			setSearchInput(sharedSearchInput);
			setSearchTermTable(sharedSearchInput);
			setPage(1);
		}
	}, [sharedSearchInput]);

	const useHeaderGridActions = hasTaskPage && isAdmin;

	useEffect(() => {
		if (!hasTaskPage || !isAdmin) {
			setTaskHeaderPrimaryAction(null);
			return () => setTaskHeaderPrimaryAction(null);
		}

		setTaskHeaderPrimaryAction({
			label: "New Task",
			onClick: () => setOpenNewTaskDialog(true),
		});

		return () => setTaskHeaderPrimaryAction(null);
	}, [hasTaskPage, isAdmin, setTaskHeaderPrimaryAction]);

	const { timezone } = useTime();
	const groupNameById = useMemo(
		() => new Map(taskGroupsStabilised.map((group) => [group.Id, group.Name])),
		[taskGroupsStabilised],
	);
	const userNameById = useMemo(
		() => new Map(users.map((user) => [user.Id, user.Name])),
		[users],
	);

	const mapTaskToDialogRow = useCallback(
		function mapTaskToDialogRow(
			task: TaskDto | null | undefined,
		): TaskRow | null {
			if (!task || !Number.isInteger(task.TaskId) || task.TaskId <= 0)
				return null;

			const groupResponsibleIds = task.GroupResponsibleList?.length
				? task.GroupResponsibleList
				: typeof task.TaskGroupId === "number"
					? [task.TaskGroupId]
					: [];
			const userResponsibleIds = task.UserResponsibleList?.length
				? task.UserResponsibleList
				: task.User?.Id
					? [task.User.Id]
					: [];

			const responsibleGroupNames = groupResponsibleIds
				.map((groupId) => groupNameById.get(groupId))
				.filter((name): name is string => Boolean(name));

			const responsibleUserNames = userResponsibleIds
				.map((userId) => userNameById.get(userId))
				.map((name) => formatDisplayName(name))
				.filter((name): name is string => Boolean(name));
			const assignedUserName = formatDisplayName(
				(task.User?.Id ? userNameById.get(task.User.Id) : undefined) ??
					task.User?.Name,
			);

			const recurringChildren: RecurringChildSummary[] = [];
			for (const child of task.RecurringTasks ?? []) {
				if (!Number.isInteger(child.TaskId) || child.TaskId <= 0) continue;
				recurringChildren.push({
					id: child.TaskId,
					title: child.Title ?? "",
					status: child.Status ?? "",
					deadline: child.Deadline ?? null,
				});
			}

			return {
				id: task.TaskId,
				title: task.Title ?? "",
				deadline: task.Deadline,
				description: task.Description ?? "",
				status: task.Status ?? "",
				lastChanged:
					task.LastChanged && dayjs(task.LastChanged).isValid()
						? dayjs(task.LastChanged).toDate()
						: null,
				recurring: task.Reccuring ? "Yes" : "No",
				frequency: task.ReccuringConfiguration?.Frequency ?? "",
				shiftlog: task.Shiftlog ? "Yes" : "No",
				handover: task.Handover ? "Yes" : "No",
				user: assignedUserName,
				tags: task.Tags ?? [],
				attachment: task.Attachment ?? null,
				comments: task.Comments ?? [],
				completed: null,
				completedBy: null,
				selectedSystem: null,
				groupName:
					(typeof task.TaskGroupId === "number"
						? groupNameById.get(task.TaskGroupId)
						: undefined) ?? "",
				groupResponsibleCount: groupResponsibleIds.length,
				userResponsibleCount: userResponsibleIds.length,
				responsibleGroups: responsibleGroupNames,
				responsibleUsers: responsibleUserNames,
				recurringTemplateId:
					task.ReccuringConfiguration?.ReccuringTemplateId ?? null,
				recurringChildren,
			} satisfies TaskRow;
		},
		[groupNameById, userNameById],
	);

	const handleGroupChange = (event: SelectChangeEvent<number[]>) => {
		const value = event.target.value;
		const groupIds = Array.isArray(value)
			? value
					.map((id) => Number(id))
					.filter((id) => Number.isInteger(id) && id > 0)
			: [];
		setSelectedGroupIds(groupIds);
		setPage(1);
	};

	const taskItems = useMemo<TaskDto[]>(() => {
		if (isTasksResponse(data)) return data.Items;
		if (isLegacyTasksResponse(data)) return data.Tasks ?? [];
		return [];
	}, [data]);

	const rows = useMemo(() => {
		if (stableGroupIds.length === 0) return [];
		const mappedRows: TaskRow[] = [];
		for (const task of taskItems) {
			const mappedTask = mapTaskToDialogRow(task);
			if (mappedTask) {
				mappedRows.push(mappedTask);
			}
		}
		const uniqueRowsById = new Map<number, TaskRow>();
		for (const row of mappedRows) {
			if (!uniqueRowsById.has(row.id)) {
				uniqueRowsById.set(row.id, row);
			}
		}
		return [...uniqueRowsById.values()];
	}, [mapTaskToDialogRow, stableGroupIds.length, taskItems]);

	// Pagination & export
	const totalItems =
		stableGroupIds.length === 0
			? 0
			: typeof data?.Total === "number"
				? data.Total
				: rows.length;
	const fileNameExport = useMemo(
		() => `RecurringTasks_${dayjs().format("YYYY-MM-DD_HH-mm")}`,
		[],
	);
	const taskIdFromUrl = useMemo(() => {
		const raw = searchParams.get("taskId");
		if (!raw) return null;
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	}, [searchParams]);
	const parentTaskIdFromUrl = useMemo(() => {
		const raw = searchParams.get("parentTaskId");
		if (!raw) return null;
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	}, [searchParams]);
	const recurringTaskByIdResult = useFetchTaskById(
		taskIdFromUrl ?? 0,
	) as {
		data?: TaskDto;
	};
	const { data: selectedRecurringTaskById } = recurringTaskByIdResult;
	const hydratedRecurringTask = useMemo<TaskRow | null>(
		() => mapTaskToDialogRow(selectedRecurringTaskById),
		[mapTaskToDialogRow, selectedRecurringTaskById],
	);
	const rowsById = useMemo(
		() => new Map(rows.map((row) => [Number(row.id), row])),
		[rows],
	);
	const taskTitlesById = useMemo(() => {
		const titles = new Map<number, string>();
		for (const row of rows) {
			titles.set(Number(row.id), row.title);
			for (const child of row.recurringChildren ?? []) {
				if (child.title) titles.set(Number(child.id), child.title);
			}
		}
		if (hydratedRecurringTask?.id && hydratedRecurringTask.title) {
			titles.set(Number(hydratedRecurringTask.id), hydratedRecurringTask.title);
		}
		for (const child of hydratedRecurringTask?.recurringChildren ?? []) {
			if (child.title) titles.set(Number(child.id), child.title);
		}
		return titles;
	}, [hydratedRecurringTask, rows]);
	const getTaskRowSignature = useCallback(
		(task: TaskRow | null | undefined) =>
			task
				? JSON.stringify({
						id: task.id,
						title: task.title,
						description: task.description,
						status: task.status,
						deadline: task.deadline ?? null,
						lastChanged: String(task.lastChanged ?? ""),
						tags: task.tags,
						groupName: task.groupName,
						frequency: task.frequency,
						shiftlog: task.shiftlog,
						handover: task.handover,
						responsibleGroups: task.responsibleGroups,
						responsibleUsers: task.responsibleUsers,
						recurringTemplateId: task.recurringTemplateId ?? null,
						recurringChildren: (task.recurringChildren ?? []).map((child) => ({
							id: child.id,
							title: child.title,
							status: child.status ?? "",
							deadline: child.deadline ?? null,
						})),
					})
				: "",
		[],
	);

	const syncTaskIdInUrl = (
		taskId: number | null,
		parentTaskId: number | null = null,
	) => {
		const next = new URLSearchParams(searchParams);
		if (taskId == null) next.delete("taskId");
		else next.set("taskId", String(taskId));
		if (parentTaskId == null) next.delete("parentTaskId");
		else next.set("parentTaskId", String(parentTaskId));
		setSearchParams(next, { replace: true });
	};

	const setTaskDialogState = (open: boolean) => {
		setTaskDialogOpen(open);
		if (!open) {
			setRecurringParentContextTaskId(null);
			syncTaskIdInUrl(null);
			return;
		}
		if (selectedTask?.id) {
			syncTaskIdInUrl(
				Number(selectedTask.id),
				recurringParentContextTaskId ?? null,
			);
		}
	};

	const openTaskDialogForRow = (row: TaskRow) => {
		setRecurringParentContextTaskId(null);
		setSelectedTask(row);
		setNewComment("");
		setTaskDialogOpen(true);
		if (row?.id) {
			syncTaskIdInUrl(Number(row.id));
		}
	};

	const navigateToRecurringTask = useCallback(
		(taskId: number, parentTaskId?: number | null) => {
			const matchedRow = rowsById.get(Number(taskId)) ?? null;
			setRecurringParentContextTaskId(parentTaskId ?? null);
			setSelectedTask(matchedRow ?? null);
			setNewComment("");
			setTaskDialogOpen(true);
			syncTaskIdInUrl(taskId, parentTaskId ?? null);
		},
		[rowsById, syncTaskIdInUrl],
	);

	const resolveRecurringTaskTitle = useCallback(
		(taskId: number) => {
			if (Number(selectedTask?.id) === Number(taskId)) return selectedTask?.title;
			return taskTitlesById.get(Number(taskId));
		},
		[selectedTask?.id, selectedTask?.title, taskTitlesById],
	);

	// ── Highlight & text helpers ─────────────────────────────────────────────
	const highlight = (text?: string, term?: string) => {
		const t = (text ?? "").toString();
		const q = (term ?? "").trim().toLowerCase();
		if (!q) return t;
		const idx = t.toLowerCase().indexOf(q);
		if (idx === -1) return t;

		const before = t.slice(0, idx);
		const match = t.slice(idx, idx + q.length);
		const after = t.slice(idx + q.length);

		return (
			<>
				{before}
				<Box
					component="mark"
					sx={{
						px: 0.25,
						borderRadius: 0.5,
						backgroundColor: alpha(theme.palette.warning.main, 0.28),
					}}
				>
					{match}
				</Box>
				{after}
			</>
		);
	};

	const stripHtml = (s?: string) =>
		s
			? s
					.replace(/<[^>]*>/g, " ")
					.replace(/\s+/g, " ")
					.trim()
			: "";

	const truncate = (s: string, n = 160) =>
		s.length > n ? `${s.slice(0, n - 1)}…` : s;

	const fmt = (d?: string | number | Date | dayjs.Dayjs | null) =>
		d ? dayjs.utc(d).tz(timezone).format("DD/MM/YY HH:mm:ss") : "";

	// helper near the columns
	const asCommentsArray = (row: TaskRow & { Comments?: TaskCommentDto[] }) =>
		Array.isArray(row?.comments)
			? row.comments
			: Array.isArray(row?.Comments)
				? row.Comments
				: [];

	const renderMobileTaskCard = (row: TaskRow) => {
		const deadlineLabel = row.deadline ? fmt(row.deadline) : "No deadline";
		const changedLabel = row.lastChanged ? fmt(row.lastChanged) : "No changes";
		const tags = Array.isArray(row.tags) ? row.tags : [];
		const quickFlags = [
			row.frequency ? `Every ${row.frequency}` : null,
			row.handover === "Yes" ? "Handover" : null,
			row.shiftlog === "Yes" ? "Shiftlog" : null,
		].filter(Boolean) as string[];

		return (
			<Card
				key={`mobile-${row.id}`}
				variant="outlined"
				onClick={() => openTaskDialogForRow(row)}
				sx={{
					borderRadius: 2,
					borderColor: alpha(theme.palette.divider, 0.9),
					bgcolor: "background.paper",
					cursor: "pointer",
					transition: "background-color 120ms ease, border-color 120ms ease",
					"&:active": {
						bgcolor: alpha(theme.palette.primary.main, 0.04),
					},
				}}
			>
				<Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-start",
							justifyContent: "space-between",
							gap: 1,
						}}
					>
						<Box sx={{ minWidth: 0, flex: 1 }}>
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 800,
									display: "-webkit-box",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									overflow: "hidden",
								}}
							>
								{highlight(row.title, searchInput)}
							</Typography>
						</Box>
						<Box
							sx={{
								width: 32,
								height: 32,
								display: "grid",
								placeItems: "center",
								borderRadius: 2,
								bgcolor: alpha(theme.palette.primary.main, 0.08),
								color: "primary.main",
								flexShrink: 0,
							}}
						>
							<ArrowOutwardIcon sx={{ fontSize: 18 }} />
						</Box>
					</Box>

					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
						}}
					>
						{truncate(stripHtml(row.description) || "No description", 120)}
					</Typography>

					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
						<Chip
							size="small"
							label={`Next due ${deadlineLabel}`}
							sx={{ borderRadius: 2, maxWidth: "100%" }}
						/>
						<Chip
							size="small"
							label={`Updated ${changedLabel}`}
							sx={{ borderRadius: 2, maxWidth: "100%" }}
						/>
					</Box>

					<Typography variant="caption" color="text.secondary">
						{row.user || "Unassigned"}
					</Typography>

					{(quickFlags.length > 0 || tags.length > 0) && (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
							{quickFlags.map((flag) => (
								<Chip
									key={`${row.id}-${flag}`}
									size="small"
									label={flag}
									sx={{
										borderRadius: 2,
										bgcolor: alpha(theme.palette.primary.main, 0.08),
										color: "primary.main",
									}}
								/>
							))}
							{tags.slice(0, 2).map((tag: string) => (
								<Chip
									key={`${row.id}-${tag}`}
									size="small"
									label={tag}
									sx={{ borderRadius: 2 }}
								/>
							))}
							{tags.length > 2 && (
								<Chip
									size="small"
									label={`+${tags.length - 2}`}
									sx={{ borderRadius: 2 }}
								/>
							)}
						</Box>
					)}
				</Box>
			</Card>
		);
	};

	useEffect(() => {
		if (taskIdFromUrl == null) {
			if (taskDialogOpen) setTaskDialogOpen(false);
			return;
		}

		// Dialog navigation can switch selected task before URL sync catches up.
		// Avoid forcing selection back from stale URL during that transition.
		if (
			taskDialogOpen &&
			selectedTask?.id &&
			Number(selectedTask.id) !== taskIdFromUrl
		) {
			return;
		}

		const matchedRow = rowsById.get(taskIdFromUrl);
		const preferredTask = hydratedRecurringTask ?? matchedRow;
		if (!preferredTask) return;
		setRecurringParentContextTaskId(parentTaskIdFromUrl);

		if (!taskDialogOpen) {
			setSelectedTask(preferredTask);
			setTaskDialogOpen(true);
			return;
		}

		if (
			Number(selectedTask?.id) === taskIdFromUrl &&
			getTaskRowSignature(selectedTask) === getTaskRowSignature(preferredTask)
		) {
			return;
		}
		setSelectedTask(preferredTask);
	}, [
		hydratedRecurringTask,
			getTaskRowSignature,
			rowsById,
			parentTaskIdFromUrl,
			taskIdFromUrl,
			taskDialogOpen,
			selectedTask,
		]);

	useEffect(() => {
		if (!taskDialogOpen || !selectedTask?.id) return;
		syncTaskIdInUrl(
			Number(selectedTask.id),
			recurringParentContextTaskId ?? null,
		);
	}, [taskDialogOpen, selectedTask?.id, recurringParentContextTaskId]);

	// Columns
	const columns = useMemo(
		() => [
				{
					field: "frequency",
					headerName: "Frequency",
					flex: 1.1,
					headerAlign: "left",
					align: "left",
					headerClassName: "leftAlignedColumnHeader",
					cellClassName: "leftAlignedCell",
					sortable: false,
					renderCell: ({ row }: TaskCellParams) => (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								minWidth: 0,
								width: "100%",
								height: "100%",
								py: 0.25,
								textAlign: "left",
							}}
						>
							<Typography
								variant="body2"
								fontWeight={700}
								noWrap
								sx={{ lineHeight: 1.25, textAlign: "left" }}
							>
								{row.frequency || "—"}
							</Typography>
							{/* <Typography
								variant="caption"
								color="text.secondary"
								noWrap
								sx={{ mt: 0.2, lineHeight: 1.15, textAlign: "left" }}
							>
								{row.deadline
									? `Next due ${dayjs.utc(row.deadline).tz(timezone).format("DD/MM/YY HH:mm")}`
									: "No deadline"}
							</Typography> */}
						</Box>
					),
				},
				{
					field: "title",
					headerName: "Title",
					flex: 2.2,
					headerAlign: "left",
					align: "left",
					headerClassName: "leftAlignedColumnHeader",
					cellClassName: "leftAlignedCell",
					sortable: false,
					renderCell: ({ row }: TaskCellParams) => (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								minWidth: 0,
								width: "100%",
								height: "100%",
								py: 0.125,
								textAlign: "left",
							}}
						>
							<Typography
								variant="body2"
								fontWeight={700}
								noWrap
								sx={{ lineHeight: 1.25 }}
							>
								{highlight(row.title, searchTermTable)}
							</Typography>
						</Box>
					),
				},
				{
					field: "description",
					headerName: "Description",
					flex: 2.2,
					headerAlign: "left",
					align: "left",
					headerClassName: "leftAlignedColumnHeader",
					cellClassName: "leftAlignedCell",
					sortable: false,
					renderCell: ({ value, row }: TaskCellParams) => {
						const rawValue = typeof value === "string" ? value : "";
					const hasSearch = !!searchTermTable.trim();
						if (hasSearch) {
							const plain = stripHtml(rawValue);
							const preview = truncate(plain, 220);
							return (
								<Tooltip title={highlight(plain, searchTermTable)} arrow>
									<Box sx={{ width: "100%", minWidth: 0, textAlign: "left" }}>
										<Typography
											variant="body2"
											sx={{
												whiteSpace: "normal",
												display: "-webkit-box",
												WebkitLineClamp: 3,
												WebkitBoxOrient: "vertical",
												overflow: "hidden",
												textAlign: "left",
											}}
										>
											{highlight(preview, searchTermTable)}
										</Typography>
									</Box>
								</Tooltip>
							);
						}
					const plain = stripHtml(rawValue);
					const preview = truncate(plain, 220);
					return (
						<Tooltip
							title={
								<SATOSContentRender
									isNotProcedureRender={true}
									rehypePlugins={[rehypeRaw]}
									content={rawValue}
								/>
							}
							arrow
						>
							<Box sx={{ minWidth: 0, width: "100%", textAlign: "left" }}>
								<Typography
									variant="body2"
									sx={{
										display: "-webkit-box",
										WebkitLineClamp: 3,
										WebkitBoxOrient: "vertical",
										overflow: "hidden",
										lineHeight: 1.45,
										textAlign: "left",
									}}
								>
									{preview || "No description"}
								</Typography>
								{row?.attachment?.FileName ? (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ mt: 0.35, display: "block", textAlign: "left" }}
									>
										{`${GetLanguageValueAsString(
											translation,
											"RecurringTasks.Attachment",
											"Attachment",
										)}: ${row.attachment.FileName}`}
									</Typography>
								) : null}
							</Box>
						</Tooltip>
					);
				},
			},
				// {
				// 	field: "lastChanged",
				// 	headerName: "Last Changed",
				// 	flex: 1.2,
				// 	sortable: false,
				// valueFormatter: (input: unknown) => {
				// 	const value = extractGridValue(input);
				// 	return fmt(
				// 		typeof value === "string" || value instanceof Date ? value : null,
				// 	);
				// },
				// 	renderCell: ({ value }: TaskCellParams) => {
				// 		const parsedValue =
				// 			typeof value === "string" || value instanceof Date ? value : null;
				// 		return (
				// 			<Box
				// 				sx={{
				// 					display: "flex",
				// 					alignItems: "center",
				// 					justifyContent: "center",
				// 					height: "100%",
				// 					minWidth: 0,
				// 					width: "100%",
				// 					py: 0.125,
				// 					textAlign: "center",
				// 				}}
				// 			>
				// 				<Typography
				// 					variant="caption"
				// 					color="text.secondary"
				// 					sx={{ lineHeight: 1.15 }}
				// 				>
				// 					{parsedValue
				// 						? dayjs
				// 								.utc(parsedValue)
				// 								.tz(timezone)
				// 								.format("DD/MM/YY HH:mm")
				// 						: "—"}
				// 				</Typography>
				// 			</Box>
				// 		);
				// 	},
				// },
			{
				field: "recurring",
				headerName: "Recurring",
				flex: 0.8,
				sortable: false,
				renderCell: (params: TaskCellParams) => (
					<Checkbox
						checked={params.value === "Yes"}
						disabled
						size="small"
						sx={sharedTableCheckboxSx(theme)}
					/>
				),
			},
			{
				field: "attachment",
				headerName: "Attachment",
				flex: 1.4,
				sortable: false,
				renderCell: ({ row }: TaskCellParams) =>
					row.attachment?.Url ? (
						<Link
							href={row.attachment.Url}
							target={isImageAttachment(row.attachment) ? undefined : "_blank"}
							rel="noopener noreferrer"
							onClick={(event) => {
								if (!isImageAttachment(row.attachment)) return;
								event.preventDefault();
								setImageAttachmentPreview({
									url: row.attachment?.Url ?? "",
									fileName: row.attachment?.FileName ?? "Image attachment",
								});
							}}
							underline="hover"
							sx={{
								display: "inline-flex",
								alignItems: "center",
								minWidth: 0,
								overflow: "hidden",
							}}
						>
							<ArrowOutwardIcon fontSize="small" sx={{ mr: 0.5 }} />
							<Typography
								variant="body2"
								noWrap
								sx={{ textOverflow: "ellipsis" }}
							>
								{highlight(row.attachment.FileName ?? "", searchTermTable)}
							</Typography>
						</Link>
					) : (
						<Typography variant="body2" color="text.disabled">
							—
						</Typography>
					),
			},
			{
				field: "comments",
				headerName: "Comments",
				flex: 1.4,
				sortable: false,
				valueGetter: ({ row }: TaskCellParams) => {
					const list = asCommentsArray(row);
					if (!list.length) return "";
					return list
						.map((comment) => {
							const text = stripHtml(comment?.Text ?? comment?.Value ?? "");
							const when =
								comment?.Created || comment?.Date
									? dayjs
											.utc(comment.Created ?? comment.Date)
											.tz(timezone)
											.format("DD/MM/YY HH:mm:ss")
									: "";
							return when ? `${text} (${when})` : text;
						})
						.join(" | ");
				},
				renderCell: ({ row }: TaskCellParams) => {
					const list = asCommentsArray(row);
					if (!list.length) {
						return (
							<Typography variant="caption" color="text.disabled">
								<Translate String="Auto.Features.Task.Pages.RecurringTasks.L1141C60.NoComments">No comments</Translate>
							</Typography>
						);
					}
					const hasSearch = !!searchTermTable.trim();
					const tooltipItems = list.map((comment, index) => {
						const when =
							comment?.Date || comment?.Created
								? dayjs(comment.Date ?? comment.Created).format(
										"DD/MM/YY HH:mm:ss",
									)
								: "Unknown";

						if (hasSearch) {
							const plain = stripHtml((comment.Value ?? comment.Text) || "");
							const preview = truncate(plain, 220);

							return (
								<Box key={index}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
									>
										{when}
									</Typography>
									<Typography variant="body2" sx={{ whiteSpace: "normal" }}>
										{highlight(preview, searchTermTable)}
									</Typography>
								</Box>
							);
						}

						return (
							<Box key={index}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
								>
									{when}
								</Typography>
								<Box
									sx={{
										fontSize: theme.typography.body2.fontSize,
										lineHeight: 1.45,
										"& p": { m: 0 },
										"& p + p": { mt: 0.5 },
										"& *": { maxWidth: "100%" },
										"& a": {
											wordBreak: "break-word",
											overflowWrap: "anywhere",
										},
									}}
								>
									<SATOSContentRender
										isNotProcedureRender={true}
										rehypePlugins={[rehypeRaw]}
										content={comment.Value ?? comment.Text}
									/>
								</Box>
							</Box>
						);
					});

					return <DataGridCommentTooltip items={tooltipItems} />;
				},
			},
			{
				field: "shiftlog",
				headerName: "Shiftlog",
				flex: 0.8,
				sortable: false,
				renderCell: (params: TaskCellParams) => (
					<Checkbox
						checked={params.value === "Yes"}
						disabled
						size="small"
						sx={sharedTableCheckboxSx(theme)}
					/>
				),
			},
			{
				field: "handover",
				headerName: "Handover",
				flex: 0.8,
				sortable: false,
				renderCell: (params: TaskCellParams) => (
					<Checkbox
						checked={params.value === "Yes"}
						disabled
						size="small"
						sx={sharedTableCheckboxSx(theme)}
					/>
				),
			},
			{
				field: "tags",
				headerName: "Tags",
				flex: 1.6,
				sortable: false,
				renderCell: ({ row }: TaskCellParams) => (
					<Box
						sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, minWidth: 0 }}
					>
						{row.tags.map((tag: string, idx: number) => (
							<Chip
								key={idx}
								label={
									<Typography variant="caption" noWrap>
										{highlight(tag, searchTermTable)}
									</Typography>
								}
								size="small"
								sx={sharedTableTagChipSx(theme)}
							/>
						))}
					</Box>
				),
			},
			{
				field: "user",
				headerName: "Name",
				flex: 1,
				headerAlign: "left",
				align: "left",
				headerClassName: "leftAlignedColumnHeader",
				cellClassName: "leftAlignedCell",
				sortable: false,
				renderCell: ({ row, value }: TaskCellParams) => (
					<Box sx={{ minWidth: 0, width: "100%", textAlign: "left" }}>
						<Typography variant="body2" fontWeight={700} noWrap>
							{highlight(
								typeof value === "string" ? value : "Unassigned",
								searchTermTable,
							)}
						</Typography>
						<Typography variant="caption" color="text.secondary" noWrap>
							{row.userResponsibleCount ?? 0} <Translate String="Auto.Features.Task.Pages.RecurringTasks.L1276C39.User">user</Translate>
							{row.userResponsibleCount === 1 ? "" : "s"} •{" "}
							{row.groupResponsibleCount ?? 0} <Translate String="Auto.Features.Task.Pages.RecurringTasks.L1278C40.Group">group</Translate>
							{row.groupResponsibleCount === 1 ? "" : "s"}
						</Typography>
					</Box>
				),
			},
		],
		[timezone, searchTermTable, theme],
	);
	const { apiRef, pdfDialog, toolbarProps } = useTaskTableHeaderActions({
		setTaskHeaderActions,
		isMobile,
		rows,
		columns,
		fileName: fileNameExport,
		enableGridTools: useHeaderGridActions,
	});

	return (
		<Card
			elevation={0}
			sx={{
				width: "100%",
				flex: 1,
				minHeight: 0,
				borderRadius: 2,
				border: (t) => `1px solid ${t.palette.divider}`,
				m: { xs: 1, sm: 1.5, md: 2 },
				bgcolor: COL.cardBg(theme),
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<CardContent
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					p: 2,
				}}
			>
				{/* Top Controls */}
				<Box
					sx={{
						pt: taskPage ? 0 : 0.5,
						pb: taskPage ? 0 : 1.5,
						bgcolor: COL.cardBg(theme),
						flexShrink: 0,
					}}
				>
					{!taskPage && (
						<Grid container spacing={1.5} alignItems="center">
							{isAdmin && !taskPage && (
								<Grid item xs={12} sm="auto">
									<Button
										variant="contained"
										color="info"
										onClick={() => setOpenNewTaskDialog(true)}
										sx={{ textTransform: "none", fontWeight: 800 }}
									>
										+{" "}
										<Translate String="RecurringTasks.NewTask">
											New Task
										</Translate>
									</Button>
								</Grid>
							)}

							<Snackbar
								open={showGroupWarning}
								autoHideDuration={3000}
								onClose={() => setShowGroupWarning(false)}
								anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
							>
								<Alert
									onClose={() => setShowGroupWarning(false)}
									severity="warning"
									sx={{ width: "100%" }}
								>
									<Translate String="RecurringTasks.PleaseSelectAtLeastOneGroup">
										Please select at least one group.
									</Translate>
								</Alert>
							</Snackbar>

							{!taskPage && (
								<Grid item xs={12} md={4}>
									<FormControl
										fullWidth
										size="small"
										variant="outlined"
										error={stableGroupIds.length === 0}
										sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
									>
										<InputLabel>
											<Translate String="RecurringTasks.Group">Group</Translate>
										</InputLabel>
										<Select
											disabled={!taskGroupsStabilised.length}
											multiple
											value={stableGroupIds}
											onChange={handleGroupChange}
											label={
												<Translate String="RecurringTasks.Group">
													Group
												</Translate>
											}
											renderValue={(selected) => (
												<Box
													sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
												>
													{(Array.isArray(selected) ? selected : []).map(
														(id) => {
															const group = taskGroupsStabilised.find(
																(candidate) => candidate.Id === Number(id),
															);
															return group ? (
																<Chip
																	key={group.Id}
																	label={group.Name}
																	size="small"
																	sx={{ bgcolor: COL.chipBg, fontWeight: 700 }}
																/>
															) : null;
														},
													)}
												</Box>
											)}
											MenuProps={{
												PaperProps: {
													elevation: 3,
													sx: { borderRadius: 2, mt: 0.5 },
												},
											}}
										>
											{taskGroupsStabilised.map((group) => (
												<MenuItem key={group.Id} value={group.Id}>
													{group.Name}
												</MenuItem>
											))}
										</Select>
										{stableGroupIds.length === 0 && (
											<FormHelperText>
												<Translate String="RecurringTasks.PleaseSelectAtLeastOneGroup">
													Please select at least one group
												</Translate>
											</FormHelperText>
										)}
									</FormControl>
								</Grid>
							)}

							{!taskPage && (
								<Grid item xs={12} md>
									<Box display="flex" gap={1}>
										<TextField
											fullWidth
											size="small"
											variant="outlined"
											disabled={stableGroupIds.length === 0 || isFetching}
											label={
												<Translate String="RecurringTasks.SearchTasks">
													Search Tasks
												</Translate>
											}
											value={searchInput}
											onChange={(e) => setSearchInput(e.target.value)}
											sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
										/>
										<Button
											startIcon={<SearchIcon />}
											variant="outlined"
											color="primary"
											disabled={stableGroupIds.length === 0 || isFetching}
											onClick={() => {
												setSearchTermTable(searchInput);
												setPage(1);
											}}
											sx={{ borderRadius: 2, fontWeight: 800, minWidth: 110 }}
										>
											<Translate String="RecurringTasks.Search">
												Search
											</Translate>
										</Button>
									</Box>
								</Grid>
							)}
						</Grid>
					)}
				</Box>

				{/* Data Grid Section */}
				<Card
					variant="outlined"
					sx={{
						flex: 1,
						minHeight: 0,
						borderRadius: 2,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
						mb: 1.5,
					}}
				>
					<Box
						sx={{
							...sharedAutoHeightDataGridSectionSx(theme),
						}}
					>
						<ErrorBoundary
							fallbackRender={({ error, resetErrorBoundary }) => {
								const safeReset = () => {
									if (typeof resetErrorBoundary === "function") {
										(resetErrorBoundary as () => void)();
									}
								};
								return (
									<ComponentError
										message={
											error instanceof Error
												? error.message
												: "Something went wrong."
										}
										reset={safeReset}
									/>
								);
							}}
						>
							<Suspense fallback={<Spinner text={GetLanguageValueAsString(translation, "Auto.Features.Task.Pages.RecurringTasks.L1510C42.Loading", "Loading")} />}>
								<Box
									sx={{
										flex: 1,
										minHeight: 0,
										display: "flex",
										flexDirection: "column",
									}}
								>
									<Box
										sx={{
											width: "100%",
										}}
									>
										{isMobile ? (
											<Box
												sx={{
													width: "100%",
													display: "flex",
													flexDirection: "column",
													gap: 1,
													p: 0.25,
												}}
											>
												{rows.map((row) => renderMobileTaskCard(row))}
											</Box>
										) : (
											<StyledDataGrid
												apiRef={apiRef}
												autoHeight
												rows={rows}
												rowCount={totalItems}
												columns={columns}
												showToolbar={isAdmin}
												getRowHeight={() => "auto"}
												disableVirtualization
												getRowClassName={(params) => {
													const index = params.indexRelativeToCurrentPage ?? 0;
													return index % 2 === 0 ? "row-even" : "row-odd";
												}}
												hideFooterPagination
												sortingMode="server"
												sortingOrder={["asc", "desc"]}
												sortModel={[
													{
														field: "deadline",
														sort:
															sortDirection === SortDirection.DESC
																? "desc"
																: "asc",
													},
												]}
												onSortModelChange={(model) => {
													if (
														model.length > 0 &&
														model[0].field === "deadline"
													) {
														setSortDirection(
															model[0].sort === "desc"
																? SortDirection.DESC
																: SortDirection.ASC,
														);
														setPage(1);
													}
												}}
												loading={isFetching}
												disableRowSelectionOnClick
												onRowClick={(params) => {
													openTaskDialogForRow(params.row as TaskRow);
												}}
												slots={
													isAdmin ? { toolbar: CustomToolbarV2 } : undefined
												}
												slotProps={
													useHeaderGridActions
														? { toolbar: toolbarProps }
														: undefined
												}
													sx={{
														width: "100%",
														maxWidth: "100%",
														"& .MuiDataGrid-columnHeader": {
															justifyContent: "center",
														},
														"& .MuiDataGrid-columnHeaderTitleContainer": {
															justifyContent: "center",
														},
														"& .MuiDataGrid-columnHeaderTitleContainerContent": {
															justifyContent: "center",
															width: "100%",
														},
														"& .MuiDataGrid-columnHeaderTitle": {
															width: "100%",
															textAlign: "center",
														},
														"& .MuiDataGrid-cell": {
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															textAlign: "center",
														},
														"& .leftAlignedColumnHeader": {
															justifyContent: "flex-start",
														},
														"& .leftAlignedColumnHeader .MuiDataGrid-columnHeaderTitleContainer": {
															justifyContent: "flex-start",
														},
														"& .leftAlignedColumnHeader .MuiDataGrid-columnHeaderTitleContainerContent": {
															justifyContent: "flex-start",
														},
														"& .leftAlignedColumnHeader .MuiDataGrid-columnHeaderTitle": {
															textAlign: "left",
														},
														"& .leftAlignedCell": {
															justifyContent: "flex-start",
															textAlign: "left",
														},
													}}
												/>
										)}
									</Box>

									{/* Empty state */}
									{!isFetching &&
										stableGroupIds.length > 0 &&
										rows.length === 0 && (
											<Box
												sx={{
													display: "grid",
													placeItems: "center",
													py: 8,
													textAlign: "center",
													color: "text.secondary",
												}}
											>
												<Box>
													<Typography
														variant="h6"
														sx={{ fontWeight: 800, mb: 0.5 }}
													>
														<Translate String="RecurringTasks.NoRecurringTasksFound">
															No recurring tasks found
														</Translate>
													</Typography>
													<Typography variant="body2">
														<Translate String="RecurringTasks.TryAdjustingYourSearchOrGroups">
															Try adjusting your search or groups.
														</Translate>
													</Typography>
												</Box>
											</Box>
										)}
								</Box>
							</Suspense>
						</ErrorBoundary>
					</Box>
				</Card>
			</CardContent>

			{/* Match Shift pagination footer */}
			{totalItems > 0 && (
				<Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
					<TablePagination
						component="div"
						count={totalItems}
						page={page - 1}
						onPageChange={(_event, newPage) => setPage(newPage + 1)}
						rowsPerPage={pageSize}
						onRowsPerPageChange={(event) => {
							const newSize = Number(event.target.value);
							if (newSize !== pageSize) {
								setPageSize(newSize);
								setPage(1);
							}
						}}
						rowsPerPageOptions={[10, 25, 50, 100]}
						SelectProps={{ size: "small" }}
						labelRowsPerPage={
							<Translate String="Shift.PaginationRows">
								Rows per page:
							</Translate>
						}
						labelDisplayedRows={({ from, to, count }) =>
							count !== -1 ? `${from}–${to} of ${count}` : `${from}–${to}`
						}
						showFirstButton
						showLastButton
						sx={{
							flexShrink: 0,
							borderTop: (t) => `1px solid ${t.palette.divider}`,
							bgcolor: "background.default",
							minHeight: 28,
							"& .MuiTablePagination-toolbar": {
								minHeight: 28,
								px: 1.5,
								py: 0,
							},
							"& .MuiTablePagination-selectLabel": {
								mr: 0.5,
								display: { xs: "none", sm: "block" },
							},
							"& .MuiTablePagination-displayedRows": {
								fontSize: { xs: "0.75rem", sm: "inherit" },
							},
							"& .MuiTablePagination-select": {
								py: 0,
								px: 0.75,
								minHeight: 22,
							},
							"& .MuiTablePagination-actions": { ml: 0 },
							"& .MuiIconButton-root": {
								padding: 2,
								"& svg": { fontSize: 16 },
							},
						}}
					/>
				</Box>
			)}

			{/* Edit Task Dialog */}
			<EditTaskDialog
				open={!!taskIdToEdit && !!detailedTask && !isLoadingTaskDetail}
				handleClose={() => setTaskIdToEdit(null)}
				onSaved={(savedTaskId) => {
					setTaskIdToEdit(null);
					setNewComment("");
					syncTaskIdInUrl(savedTaskId);
					setTaskDialogOpen(true);
				}}
				title={GetLanguageValueAsString(translation, "Auto.Features.Task.Pages.RecurringTasks.L1703C11.EditTask", "Edit Task")}
				taskId={taskIdToEdit!}
				detailedTask={detailedTask}
				groupOptions={taskGroupsStabilised || []}
				userOptions={users || []}
				systemOptions={shiftSystems}
				enforceFutureDeadline
			/>

			{/* New Task Dialog */}
			<AddNewTaskDialog
				key={openNewTaskDialog ? "open" : "closed"}
				open={openNewTaskDialog}
				handleClose={() => setOpenNewTaskDialog(false)}
				groupOptions={taskGroupsStabilised || []}
				systemOptions={shiftSystems || []}
				userOptions={users || []}
				title={title}
				enforceFutureDeadline
				groupsNewTask={groupsNewTask}
				setGroupsNewTask={setGroupsNewTask}
			/>

			{/* Task Info Dialog (recurring) */}
			<TaskDialogRecurring
				taskDialogOpen={taskDialogOpen}
				selectedTask={selectedTask}
				setTaskDialogOpen={setTaskDialogState}
				renderInfoItem={(label, value) => (
					<Box>
						<Typography variant="subtitle2" color="textSecondary">
							{label}
						</Typography>
						<Typography>{value}</Typography>
					</Box>
				)}
				newComment={newComment}
				setNewComment={setNewComment}
				isAdmin={isAdmin}
				setTaskIdToEdit={setTaskIdToEdit}
				onNavigateTask={navigateToRecurringTask}
				recurringParentTaskId={recurringParentContextTaskId}
				resolveTaskTitle={resolveRecurringTaskTitle}
			/>
			<ImageAttachmentPreviewDialog
				preview={imageAttachmentPreview}
				onClose={() => setImageAttachmentPreview(null)}
			/>
			{pdfDialog}
		</Card>
	);
};

const RecurringTasks: FC = () => {
	const translation: Language | null = useContext(TranslationContext);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				flex: 1,
				minHeight: 0,
				display: "flex",
			}}
		>
			<TaskCard title={GetLanguageValueAsString(translation, "Auto.Features.Task.Pages.RecurringTasks.L1765C19.Recurring", "Recurring")} />
		</Box>
	);
};

export default RecurringTasks;
